import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, RefreshCw, Split } from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation, formatCount } from '@/i18n';
import { formatNumber } from '@/i18n/plural';
import { useAuthStore } from '@/stores/authStore';
import { apiClient, CompoundLatencyBucket, CompoundSummary } from '@/utils/apiClient';

/**
 * STORY-301 (roadmap #15): admin-only statistics for compound queries —
 * the types asked and executed, which parts failed and why, and the
 * latency the split adds. Reads GET /api/analytics/compound/summary; every
 * label there comes from a closed set, so the panel maps keys to dictionary
 * strings and never shows query or part text.
 */

export const COMPOUND_PERIODS = [1, 7, 30, 90] as const;

const CARD = 'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700';
const TH = 'pb-1 font-medium';
const SUB = 'text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1';

type Dict = Record<string, string>;

interface CompoundStatsPanelProps {
  className?: string;
}

function label(dict: Dict | undefined, key: string, fallback: string): string {
  return dict?.[key] ?? dict?.other ?? fallback;
}

function ms(value: number | null | undefined, unit: string): string {
  return value == null ? '—' : `${Math.round(value)} ${unit}`;
}

function pct(value: number | null | undefined): string {
  return value == null ? '—' : `${(value * 100).toFixed(1)}%`;
}

/** Count rows with a plain-div bar, the RecipeHitRatePanel way (no chart lib). */
const CountTable: React.FC<{
  caption: string;
  rows: Array<[string, number]>;
  nameHeader: string;
  countHeader: string;
  barClass: string;
}> = ({ caption, rows, nameHeader, countHeader, barClass }) => {
  const max = Math.max(1, ...rows.map(([, n]) => n));
  return (
    <table className="w-full text-xs">
      <caption className={clsx(SUB, 'text-left')}>{caption}</caption>
      <thead>
        <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
          <th scope="col" className={TH}>{nameHeader}</th>
          <th scope="col" className={clsx(TH, 'text-right')}>{countHeader}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([name, n]) => (
          <tr key={name} className="border-b border-gray-50 dark:border-gray-700/50">
            <td className="py-1.5">
              <span className="text-gray-700 dark:text-gray-300">{name}</span>
              <div className="h-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden mt-1 max-w-[160px]" aria-hidden="true">
                <div className={clsx('h-full rounded-full', barClass)} style={{ width: `${(n / max) * 100}%` }} />
              </div>
            </td>
            <td className="py-1.5 text-right tabular-nums text-gray-700 dark:text-gray-300 font-medium">{n}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const CompoundStatsPanel: React.FC<CompoundStatsPanelProps> = ({ className }) => {
  const { t, language } = useTranslation();
  const at = t.analytics;
  const ct = at.compound;
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin');
  const [data, setData] = useState<CompoundSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [days, setDays] = useState<number>(7);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await apiClient.getCompoundSummary(days));
    } catch (err) {
      setError(err instanceof Error ? err.message : at.fetchError);
    } finally {
      setLoading(false);
    }
  }, [days, at.fetchError]);

  useEffect(() => {
    if (isAdmin) fetchData();
  }, [fetchData, isAdmin]);

  // The dashboard already renders this only for admins; the route is
  // admin-gated too. This keeps a stray mount from firing a 403.
  if (!isAdmin) return null;

  if (loading && !data) {
    return (
      <div className={clsx(CARD, 'p-4', className)}>
        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400" role="status">
          <RefreshCw className="h-4 w-4 animate-spin" aria-hidden="true" />
          <span className="text-sm">{at.loading}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={clsx(CARD, 'p-4', className)}>
        <div className="flex items-center justify-between" role="alert">
          <span className="text-sm text-red-500 dark:text-red-400">{error}</span>
          <button type="button" onClick={fetchData} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
            {at.refresh}
          </button>
        </div>
      </div>
    );
  }

  const total = data?.chat_response_count ?? 0;
  const coverage = data?.classifier_coverage;
  const allOff = !!coverage && coverage.total > 0 && (coverage.counts.off ?? 0) === coverage.total;
  const pf = data?.part_failure;
  const splitTurns = pf?.split_turns ?? 0;
  const bodyId = 'compound-stats-body';

  const periodLabel = (d: number) =>
    d === 1 ? ct.days1 : d === 7 ? ct.days7 : d === 30 ? ct.days30 : ct.days90;

  const classRows: Array<[string, number]> = Object.entries(data?.class_distribution.counts ?? {})
    .filter(([, n]) => n > 0)
    .map(([k, n]) => [label(ct.classes, k, k), n]);
  const planRows: Array<[string, number]> = Object.entries(data?.plan_distribution.counts ?? {})
    .filter(([, n]) => n > 0)
    .map(([k, n]) => [label(ct.plans, k, k), n]);
  // STORY-297: the LLM classifier, or the built-in follow-up rules (tier 1).
  const sourceRows: Array<[string, number]> = Object.entries(data?.plan_sources?.counts ?? {})
    .filter(([, n]) => n > 0)
    .map(([k, n]) => [label(ct.planSources, k, k), n]);
  const reasonRows: Array<[string, number]> = Object.entries(pf?.by_reason ?? {})
    .sort((a, b) => b[1] - a[1])
    .map(([k, n]) => [label(ct.reasons, k, k), n]);
  const positionLabel = (id: string) => (id === 'other' ? ct.otherLabel : `${ct.partPrefix} ${id.replace(/^p/, '')}`);
  const positionRows: Array<[string, number]> = Object.entries(pf?.by_position ?? {})
    .map(([k, n]) => [positionLabel(k), n]);
  const domainRows: Array<[string, number]> = Object.entries(pf?.by_domain ?? {})
    .sort((a, b) => b[1] - a[1])
    .map(([k, n]) => [label(ct.domains, k, k), n]);
  // Round 4: how every turn in the header total was handled. The class table
  // only covers the LLM's own verdicts (ok + shadow), so without this line a
  // window of mostly cue-screened turns read as "two thirds compound".
  const cc = coverage?.counts ?? {};
  const covScreened = cc.skipped_no_cues ?? 0;
  const covShadow = cc.shadow ?? 0;
  const covLlm = (cc.ok ?? 0) + covShadow;
  const covOff = cc.off ?? 0;
  const covError = cc.error ?? 0;
  // A status this build does not know: still a turn in the header total.
  const covUnrecorded = Math.max(0, total - (covScreened + covLlm + covOff + covError));
  // (Dropping formatNumber below is an equivalent mutant under 1000, the
  // range the tests use: it only adds the locale's digit grouping.)
  const coverageItems: Array<{ key: string; name: string; n: number }> = [
    { key: 'total', name: ct.coverageTotal, n: total },
    { key: 'screened', name: ct.coverageScreened, n: covScreened },
    { key: 'llm', name: ct.coverageLlm, n: covLlm },
    ...(covShadow > 0 ? [{ key: 'shadow', name: ct.coverageShadow, n: covShadow }] : []),
    { key: 'off', name: ct.coverageOff, n: covOff },
    { key: 'error', name: ct.coverageError, n: covError },
    ...(covUnrecorded > 0 ? [{ key: 'unrecorded', name: ct.coverageUnrecorded, n: covUnrecorded }] : []),
  ];
  const nonOk = (pf?.fail_count ?? 0) + (pf?.skipped_count ?? 0);
  const hasLegacy = (pf?.by_reason?.unrecorded ?? 0) > 0;

  const latency = data?.extra_latency;
  const hasBaseline = (latency?.baseline.count ?? 0) > 0;
  const latencyRows = (buckets: Record<string, CompoundLatencyBucket> | undefined, dict: Dict) =>
    Object.entries(buckets ?? {}).map(([k, b]) => ({ key: k, name: label(dict, k, k), b }));

  const renderBody = () => {
    if (total === 0) {
      return <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-4">{ct.empty}</p>;
    }
    if (allOff) {
      return <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-4">{ct.classifierOff}</p>;
    }
    return (
      <>
        {/* Types: what the classifier saw, and what actually ran */}
        <section aria-labelledby="compound-types" className="space-y-2">
          <h4 id="compound-types" className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {ct.sectionTypes}
          </h4>
          <div role="group" aria-labelledby="compound-coverage">
            <p className={SUB} id="compound-coverage">{ct.coverage}</p>
            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
              {coverageItems.map(({ key, name, n }) => (
                <div key={key}>
                  <dt className="text-gray-500 dark:text-gray-400">{name}</dt>
                  <dd className="font-medium tabular-nums text-gray-900 dark:text-white" data-testid={`compound-coverage-${key}`}>
                    {formatNumber(language, n)}
                  </dd>
                </div>
              ))}
            </dl>
            {(covScreened > 0 || covOff > 0) && (
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500" data-testid="compound-coverage-note">{ct.coverageNote}</p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <CountTable caption={ct.classifiedByLlm} rows={classRows} nameHeader={ct.colLabel} countHeader={ct.colCount} barClass="bg-indigo-400 dark:bg-indigo-500" />
            <CountTable caption={ct.executedPlan} rows={planRows} nameHeader={ct.colLabel} countHeader={ct.colCount} barClass="bg-indigo-400 dark:bg-indigo-500" />
            {sourceRows.length > 0 && (
              <CountTable caption={ct.plannedBy} rows={sourceRows} nameHeader={ct.colLabel} countHeader={ct.colCount} barClass="bg-indigo-400 dark:bg-indigo-500" />
            )}
          </div>
        </section>

        {/* Part failures */}
        <section aria-labelledby="compound-failures" className="space-y-2">
          <h4 id="compound-failures" className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {ct.sectionFailures}
          </h4>
          {splitTurns === 0 ? (
            <p className="text-xs text-gray-400 dark:text-gray-500">{ct.noSplits}</p>
          ) : (
            <>
              <dl className="grid grid-cols-3 gap-2 text-xs">
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">{ct.failRate}</dt>
                  <dd className="font-medium tabular-nums text-gray-900 dark:text-white" data-testid="compound-fail-rate">{pct(pf?.rate)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">{ct.failed}</dt>
                  <dd className="font-medium tabular-nums text-gray-900 dark:text-white">
                    {formatCount(language, pf?.fail_count ?? 0, at.counts.parts)}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">{ct.skipped}</dt>
                  <dd className="font-medium tabular-nums text-gray-900 dark:text-white">
                    {formatCount(language, pf?.skipped_count ?? 0, at.counts.parts)}
                  </dd>
                </div>
              </dl>
              {(pf?.skipped_count ?? 0) > 0 && (
                <p className="text-xs text-gray-400 dark:text-gray-500">{ct.skippedHint}</p>
              )}
              {nonOk === 0 ? (
                <p className="text-xs text-gray-400 dark:text-gray-500">{ct.noFailures}</p>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <CountTable caption={ct.byReason} rows={reasonRows} nameHeader={ct.colReason} countHeader={ct.colCount} barClass="bg-rose-400 dark:bg-rose-500" />
                    <CountTable caption={ct.byPosition} rows={positionRows} nameHeader={ct.colPart} countHeader={ct.colCount} barClass="bg-rose-400 dark:bg-rose-500" />
                    <CountTable caption={ct.byDomain} rows={domainRows} nameHeader={ct.colDomain} countHeader={ct.colCount} barClass="bg-rose-400 dark:bg-rose-500" />
                  </div>
                  {hasLegacy && <p className="text-xs text-gray-400 dark:text-gray-500">{ct.legacyHint}</p>}
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <caption className={clsx(SUB, 'text-left')}>{ct.recentFailures}</caption>
                      <thead>
                        <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                          <th scope="col" className={TH}>{ct.colTime}</th>
                          <th scope="col" className={TH}>{ct.colPlan}</th>
                          <th scope="col" className={TH}>{ct.colPart}</th>
                          <th scope="col" className={TH}>{ct.colDomain}</th>
                          <th scope="col" className={TH}>{ct.colReason}</th>
                          <th scope="col" className={clsx(TH, 'text-right')}>{ct.colDuration}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data?.recent_failures ?? []).map((r, i) => (
                          <tr key={`${r.created_at ?? ''}-${r.part_id}-${i}`} className="border-b border-gray-50 dark:border-gray-700/50">
                            <td className="py-1.5 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                              {r.created_at ? new Date(r.created_at).toLocaleString(language === 'en' ? 'en-US' : 'ru-RU') : '—'}
                            </td>
                            <td className="py-1.5 text-gray-700 dark:text-gray-300">{label(ct.plans, r.dispatch_plan, r.dispatch_plan)}</td>
                            <td className="py-1.5 text-gray-700 dark:text-gray-300">
                              {positionLabel(r.part_id)}
                              <span className="ml-1 text-gray-400 dark:text-gray-500">
                                ({r.status === 'skipped' ? ct.statusSkipped : ct.statusFail})
                              </span>
                            </td>
                            <td className="py-1.5 text-gray-700 dark:text-gray-300">{label(ct.domains, r.domain, r.domain)}</td>
                            <td className="py-1.5 text-gray-700 dark:text-gray-300">{label(ct.reasons, r.fail_reason, r.fail_reason)}</td>
                            <td className="py-1.5 text-right tabular-nums text-gray-500 dark:text-gray-400">
                              {/* A skipped part never ran: unknown, not 0 ms. */}
                              {r.status === 'skipped' ? '—' : ms(r.duration_ms, at.ms)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </section>

        {/* Added latency */}
        <section aria-labelledby="compound-latency" className="space-y-2">
          <h4 id="compound-latency" className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {ct.sectionLatency}
          </h4>
          <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="compound-baseline">
            {hasBaseline
              ? `${latency?.baseline.key === 'simple' ? ct.baselineSimple : ct.baselineRecipe}: ${ct.colP50} ${ms(latency?.baseline.p50_ms, at.ms)}, ${ct.colP95} ${ms(latency?.baseline.p95_ms, at.ms)}`
              : ct.noBaseline}
          </p>
          {[
            { caption: ct.byClass, rows: latencyRows(latency?.by_compound_class, ct.classes) },
            { caption: ct.byPlan, rows: latencyRows(latency?.by_dispatch_plan, ct.plans) },
          ].filter((g) => g.rows.length > 0).map((g) => (
            <div key={g.caption} className="overflow-x-auto">
              <table className="w-full text-xs">
                <caption className={clsx(SUB, 'text-left')}>{g.caption}</caption>
                <thead>
                  <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                    <th scope="col" className={TH}>{ct.colLabel}</th>
                    <th scope="col" className={clsx(TH, 'text-right')}>{ct.colCount}</th>
                    <th scope="col" className={clsx(TH, 'text-right')}>{ct.colP50}</th>
                    <th scope="col" className={clsx(TH, 'text-right')}>{ct.colP95}</th>
                    <th scope="col" className={clsx(TH, 'text-right')}>{ct.colExtraP50}</th>
                    <th scope="col" className={clsx(TH, 'text-right')}>{ct.colExtraP95}</th>
                  </tr>
                </thead>
                <tbody>
                  {g.rows.map(({ key, name, b }) => (
                    <tr key={key} className="border-b border-gray-50 dark:border-gray-700/50 tabular-nums">
                      <td className="py-1.5 text-gray-700 dark:text-gray-300">{name}</td>
                      <td className="py-1.5 text-right text-gray-700 dark:text-gray-300">{formatNumber(language, b.count)}</td>
                      <td className="py-1.5 text-right text-gray-500 dark:text-gray-400">{ms(b.p50_ms, at.ms)}</td>
                      <td className="py-1.5 text-right text-gray-500 dark:text-gray-400">{ms(b.p95_ms, at.ms)}</td>
                      <td className="py-1.5 text-right font-medium text-gray-700 dark:text-gray-300" data-testid="compound-extra-p50">
                        {hasBaseline ? ms(b.extra_p50_ms, at.ms) : '—'}
                      </td>
                      <td className="py-1.5 text-right font-medium text-gray-700 dark:text-gray-300">
                        {hasBaseline ? ms(b.extra_p95_ms, at.ms) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </section>
      </>
    );
  };

  return (
    <div className={clsx(CARD, className)} data-testid="compound-stats-panel">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        aria-controls={bodyId}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
            <Split className="h-5 w-5 text-indigo-600 dark:text-indigo-400" aria-hidden="true" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {ct.title}
              <span className="ml-2 align-middle text-[10px] font-medium uppercase text-gray-400 dark:text-gray-500">{ct.adminOnly}</span>
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {formatCount(language, total, at.counts.queries)} · {formatCount(language, splitTurns, at.counts.splitQueries)}
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-gray-400" aria-hidden="true" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
        )}
      </button>

      {expanded && (
        <div id={bodyId} className="px-4 pb-4 space-y-4 border-t border-gray-100 dark:border-gray-700 pt-4">
          <div className="flex items-center gap-2" role="group" aria-label={ct.period}>
            {COMPOUND_PERIODS.map((d) => (
              <button
                type="button"
                key={d}
                onClick={() => setDays(d)}
                aria-pressed={days === d}
                className={clsx(
                  'px-2.5 py-1 text-xs rounded-md transition-colors',
                  days === d
                    ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-medium'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                )}
              >
                {periodLabel(d)}
              </button>
            ))}
            {loading && <RefreshCw className="h-3.5 w-3.5 animate-spin text-gray-400" aria-hidden="true" />}
          </div>
          {renderBody()}
        </div>
      )}
    </div>
  );
};
